import type { DrawerContentItem } from '@/wabunka-src/common/features/Featured/models/featured/featured';

const DRAWER_TRIGGER_SELECTOR = '.drawer-trigger';

/**
 * リッチテキスト内の `.drawer-trigger` 要素クリックでドロワーを開閉する composable。
 *
 * ADR に基づき、イベント委譲で親要素のクリックを一括監視し、
 * DOM 内の `.drawer-trigger` 出現順と drawerContents 配列のインデックスを自動マッチングする。
 *
 * NOTE: トレードオフによる選択のため、他の実装の参考にしないこと。
 *
 * @param containerRef - イベント委譲の対象となるコンテナ要素の ref
 * @param drawerContents - ドロワーに表示するコンテンツ配列（API レスポンスから extractDrawerContents で抽出）
 */
export const useRichTextDrawer = (containerRef: Ref<HTMLElement | null>, drawerContents: Ref<DrawerContentItem[]>) => {
  const isOpen = ref(false);
  const activeContentIndex = ref(-1);

  const activeContent = computed<DrawerContentItem | null>(() => {
    const index = activeContentIndex.value;
    if (index < 0 || index >= drawerContents.value.length) return null;
    return drawerContents.value[index];
  });

  const openDrawer = (index: number) => {
    activeContentIndex.value = index;
    isOpen.value = true;
  };

  const closeDrawer = () => {
    isOpen.value = false;
  };

  const handleClick = (e: MouseEvent) => {
    const trigger = (e.target as Element).closest(DRAWER_TRIGGER_SELECTOR);
    if (!trigger || !containerRef.value) return;

    const allTriggers = Array.from(containerRef.value.querySelectorAll(DRAWER_TRIGGER_SELECTOR));
    const index = allTriggers.indexOf(trigger as HTMLElement);

    if (index !== -1 && index < drawerContents.value.length) {
      openDrawer(index);
    }
  };

  onMounted(() => {
    containerRef.value?.addEventListener('click', handleClick);
  });

  onBeforeUnmount(() => {
    containerRef.value?.removeEventListener('click', handleClick);
  });

  return {
    isOpen,
    activeContent,
    closeDrawer,
  };
};
